import { Random } from 'excalibur';
import { activeThemeId, getDoodleColors } from '../themes/theme';
import { CLOCK_CENTER_X, CLOCK_CENTER_Y, DESIGN_WIDTH, SEEDS } from './constants';
import { HAND_FONT } from './fonts';
import { Palette } from './palette';
import {
  css,
  drawDoodle,
  drawHeart,
  drawPaperTexture,
  drawSparkle,
  drawVignette,
  paintMiniClock,
  sketchStroke,
  wobblyLinePath,
} from './sketch';
import { GameResult } from './types';

export type ShareOutcome = 'shared' | 'downloaded' | 'canceled' | 'failed';

const CARD_WIDTH = 1080;
const CARD_HEIGHT = 1350;

let pendingCapture: HTMLImageElement | null = null;

export function rememberCapture(image: HTMLImageElement): void {
  pendingCapture = image;
}

export function takeCapture(): HTMLImageElement | null {
  const capture = pendingCapture;
  pendingCapture = null;
  return capture;
}

export function feedbackMessage(outcome: ShareOutcome): string {
  switch (outcome) {
    case 'shared':
      return 'shared! thanks for playing';
    case 'downloaded':
      return 'image saved!';
    case 'canceled':
      return 'no worries!';
    default:
      return 'could not share, sorry!';
  }
}

function sharePageUrl(result: GameResult): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const { protocol, hostname, origin } = window.location;
  if (protocol !== 'http:' && protocol !== 'https:') {
    return null;
  }
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }
  const params = new URLSearchParams();
  params.set('score', String(result.score));
  if (result.best > 0) {
    params.set('best', String(result.best));
  }
  if (result.hits > 0) {
    params.set('hits', String(result.hits));
  }
  if (result.doubles > 0) {
    params.set('doubles', String(result.doubles));
  }
  if (result.bestCombo > 0) {
    params.set('combo', String(result.bestCombo));
  }
  if (result.newBest) {
    params.set('newbest', '1');
  }
  params.set('theme', activeThemeId());
  return `${origin}/s?${params.toString()}`;
}

export async function shareScoreCard(
  result: GameResult,
  capture: HTMLImageElement | null,
): Promise<ShareOutcome> {
  const blob = await buildScoreCard(result, capture);
  if (!blob) {
    return 'failed';
  }
  const text = result.newBest
    ? `New best in Crazy Clock: ${result.score}!`
    : `I scored ${result.score} in Crazy Clock!`;
  const url = sharePageUrl(result) ?? window.location.href;
  const file = new File([blob], `crazy-clock-${Math.round(result.score)}.png`, {
    type: 'image/png',
  });
  if (
    typeof navigator.share === 'function' &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare({ files: [file] })
  ) {
    try {
      await Promise.race([
        navigator.share({ files: [file], text, title: 'Crazy Clock', url }),
        new Promise<void>((resolve) => {
          window.setTimeout(resolve, 20000);
        }),
      ]);
      return 'shared';
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return 'canceled';
      }
    }
  }
  const saved = downloadBlob(blob, file.name);
  void copyText(url ? `${text} ${url}` : text);
  return saved ? 'downloaded' : 'failed';
}

function downloadBlob(blob: Blob, filename: string): boolean {
  try {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
    return true;
  } catch {
    return false;
  }
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

async function buildScoreCard(
  result: GameResult,
  capture: HTMLImageElement | null,
): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  canvas.width = CARD_WIDTH;
  canvas.height = CARD_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }
  const rng = new Random(SEEDS.share);
  paintCardBackground(ctx, rng);
  paintCardHeading(ctx, rng, result);
  paintCardPhoto(ctx, rng, capture);
  paintCardFooter(ctx, result);
  return await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });
}

function paintCardBackground(ctx: CanvasRenderingContext2D, rng: Random): void {
  ctx.fillStyle = css(Palette.paper);
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  const washes = [
    { color: Palette.yellow, x: 140, y: 170, radius: 420 },
    { color: Palette.mint, x: 950, y: 640, radius: 460 },
    { color: Palette.pink, x: 120, y: 1090, radius: 430 },
    { color: Palette.blue, x: 980, y: 1250, radius: 380 },
  ];
  for (const wash of washes) {
    const gradient = ctx.createRadialGradient(wash.x, wash.y, 0, wash.x, wash.y, wash.radius);
    gradient.addColorStop(0, css(wash.color, 0.4));
    gradient.addColorStop(1, css(wash.color, 0));
    ctx.fillStyle = gradient;
    ctx.fillRect(wash.x - wash.radius, wash.y - wash.radius, wash.radius * 2, wash.radius * 2);
  }

  drawPaperTexture(ctx, CARD_WIDTH, CARD_HEIGHT, rng, Palette.ink, 2600);
  drawVignette(ctx, CARD_WIDTH, CARD_HEIGHT, Palette.paperEdge, 0.24);

  ctx.save();
  ctx.globalAlpha = 0.35;
  drawDoodle(ctx, 'star', 92, 96, 34, rng, Palette.inkSoft, 3.4);
  drawDoodle(ctx, 'squiggle', 986, 120, 40, rng, Palette.inkSoft, 3.2);
  const doodleColors = getDoodleColors();
  drawDoodle(ctx, 'spiral', 96, 1258, 36, rng, doodleColors[0], 3.4);
  drawDoodle(ctx, 'cloud', 972, 1256, 38, rng, doodleColors[3 % doodleColors.length], 3.2);
  ctx.restore();
}

function paintCardHeading(
  ctx: CanvasRenderingContext2D,
  rng: Random,
  result: GameResult,
): void {
  ctx.save();
  ctx.translate(CARD_WIDTH / 2, 138);
  ctx.rotate(-0.03);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = css(Palette.ink);
  ctx.font = `112px ${HAND_FONT}`;
  ctx.fillText('Crazy Clock', 0, 0);
  ctx.restore();

  sketchStroke(ctx, rng, { color: Palette.blueDeep, width: 7, alpha: 0.55 }, (c) =>
    wobblyLinePath(c, 250, 202, 830, 202, rng, 4, 14), 1);

  drawSparkle(ctx, 214, 320, 26, rng, Palette.yellowDeep, 0.8);
  drawSparkle(ctx, 872, 268, 22, rng, Palette.pinkDeep, 0.7);

  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = css(Palette.inkSoft);
  ctx.font = `48px ${HAND_FONT}`;
  ctx.fillText('i scored', CARD_WIDTH / 2, 286);
  ctx.fillStyle = css(Palette.blueDeep);
  ctx.font = `228px ${HAND_FONT}`;
  ctx.fillText(String(result.score), CARD_WIDTH / 2, 424);
  ctx.restore();

  ctx.save();
  ctx.translate(CARD_WIDTH / 2, 570);
  ctx.rotate(0.02);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = css(result.newBest ? Palette.yellowDeep : Palette.inkSoft);
  ctx.font = `54px ${HAND_FONT}`;
  ctx.fillText(result.newBest ? 'new best!' : `best ${result.best}`, 0, 0);
  ctx.restore();
}

function paintCardPhoto(
  ctx: CanvasRenderingContext2D,
  rng: Random,
  capture: HTMLImageElement | null,
): void {
  const size = 500;
  const framePad = 26;
  const frameSize = size + framePad * 2;

  ctx.save();
  ctx.translate(CARD_WIDTH / 2, 900);
  ctx.rotate(-0.045);

  ctx.fillStyle = css(Palette.shadow, 0.25);
  roundedRectPath(ctx, -frameSize / 2 + 10, -frameSize / 2 + 14, frameSize, frameSize, 16);
  ctx.fill();

  ctx.fillStyle = '#FFFDF7';
  roundedRectPath(ctx, -frameSize / 2, -frameSize / 2, frameSize, frameSize, 16);
  ctx.fill();

  if (capture) {
    const scale = capture.width / DESIGN_WIDTH;
    const focusX = CLOCK_CENTER_X * scale;
    const focusY = CLOCK_CENTER_Y * scale;
    const side = Math.min(capture.width, capture.height);
    const sx = clamp(focusX - side / 2, 0, Math.max(0, capture.width - side));
    const sy = clamp(focusY - side / 2, 0, Math.max(0, capture.height - side));
    ctx.drawImage(capture, sx, sy, side, side, -size / 2, -size / 2, size, size);
  } else {
    paintMiniClock(ctx, rng, {
      radius: size / 2 - 46,
      minuteAngle: -37,
      hourAngle: 128,
      markers: [
        { angle: -37, color: Palette.blue },
        { angle: 128, color: Palette.pink },
      ],
      face: 'happy',
    });
  }

  ctx.save();
  ctx.translate(-frameSize * 0.31, -frameSize * 0.47);
  ctx.rotate(-0.38);
  ctx.fillStyle = css(Palette.yellow, 0.7);
  ctx.fillRect(-96, -30, 192, 60);
  ctx.restore();

  ctx.save();
  ctx.translate(frameSize * 0.3, frameSize * 0.45);
  ctx.rotate(-0.32);
  ctx.fillStyle = css(Palette.mint, 0.7);
  ctx.fillRect(-84, -26, 168, 52);
  ctx.restore();

  ctx.restore();
}

function paintCardFooter(ctx: CanvasRenderingContext2D, result: GameResult): void {
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = css(Palette.inkSoft);
  ctx.font = `42px ${HAND_FONT}`;
  ctx.fillText(
    `hits ${result.hits}   doubles ${result.doubles}   best combo ${result.bestCombo}`,
    CARD_WIDTH / 2,
    1232,
  );
  ctx.fillStyle = css(Palette.ink);
  ctx.font = `56px ${HAND_FONT}`;
  const footerText = 'can you beat it?';
  ctx.fillText(footerText, CARD_WIDTH / 2, 1296);
  const footerWidth = ctx.measureText(footerText).width;
  ctx.restore();

  const heartRng = new Random(SEEDS.share + 1);
  const heartOffset = Math.min(CARD_WIDTH / 2 - 60, footerWidth / 2 + 52);
  drawHeart(ctx, CARD_WIDTH / 2 - heartOffset, 1294, 46, heartRng, Palette.heart, Palette.heartDeep, 0.9);
  drawHeart(ctx, CARD_WIDTH / 2 + heartOffset, 1294, 46, heartRng, Palette.heart, Palette.heartDeep, 0.9);
}

function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
